import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  TextField,
  Select,
  Checkbox,
  Banner,
  Badge,
  Tag,
  Box,
  RadioButton,
  Modal,
  FormLayout,
  Link,
} from '@shopify/polaris';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../providers/ApiProvider';
import { useStore } from '../../providers/StoreProvider';
import { LocationPickerModal } from '../../components/LocationPickerModal';
import {
  CatalogPickerModal,
  type CatalogItem,
} from '../../components/CatalogPickerModal';
import {
  CustomerSegmentField,
  segmentIdsFromValue,
  segmentNamesFromValue,
  segmentValueFromEligibility,
} from '../../components/CustomerSegmentField';
import {
  CampaignScheduleFields,
  defaultScheduleValue,
  scheduleFromIso,
  scheduleToIso,
  type CampaignScheduleValue,
} from '../../components/CampaignScheduleFields';
import {
  ContextualSaveBar,
  SavingOverlay,
  isShopifyEmbedded,
  showAppToast,
  useUnsavedChanges,
} from '../../components/ContextualSaveBar';
import { SelectedProductRow } from '../../components/SelectedProductRow';
import {
  CampaignNameEditor,
  campaignPageLabel,
} from '../../components/CampaignNameEditor';
import {
  generateMilestoneCode,
  resolveUniqueDiscountCode,
} from '../../lib/discountCode';
import { t } from '../../i18n';

interface ProductBlockStatus {
  active: boolean;
  statusLabel: 'Active' | 'Not added';
  blockName: string;
  blockHandle: string;
  message: string;
  deepLink: string;
  source?: string;
}

type MainStep = 1 | 2;
type TierBasis = 'cart_value' | 'product_quantity';
type RewardType = 'order_discount' | 'free_shipping' | 'free_gift';
type DistributionRule = 'nearest_only' | 'all_eligible';

interface GiftProduct {
  id: string;
  title: string;
  imageUrl?: string | null;
  quantity?: number;
  productId?: string;
  variantId?: string | null;
  selectedVariants?: Array<{
    id: string;
    title: string;
    price?: string | null;
    available?: boolean;
  }>;
  totalVariants?: number;
  subtitle?: string | null;
  outOfStock?: boolean;
}

function stripShopifyId(id: string | null | undefined): string {
  if (id == null) return '';
  const s = String(id).trim();
  if (!s) return '';
  return (
    s
      .replace(/^gid:\/\/shopify\/(Product|ProductVariant|Collection)\//, '')
      .replace(/\D/g, '') || s
  );
}

function productTitleFromCatalog(item: CatalogItem): string {
  if (item.productTitle && String(item.productTitle).trim()) {
    return String(item.productTitle).trim();
  }
  const t = String(item.title || '');
  const sep = t.indexOf(' — ');
  if (sep > 0) return t.slice(0, sep).trim();
  return t || 'Product';
}

function variantTitleFromCatalog(item: CatalogItem): string {
  if (item.kind === 'variant') {
    const t = String(item.title || '');
    const sep = t.indexOf(' — ');
    if (sep > 0) return t.slice(sep + 3).trim() || 'Default';
  }
  return 'Default';
}

function giftSubtitle(g: GiftProduct): string | null {
  const n = g.selectedVariants?.length || 0;
  const total = g.totalVariants || 0;
  if (n === 0) {
    if (total > 1) return `All ${total} variants`;
    return null;
  }
  if (total > 1) {
    if (n >= total) return `All ${total} variants`;
    return `${n} of ${total} variants`;
  }
  if (n === 1) {
    const vt = g.selectedVariants![0].title;
    if (vt && vt !== 'Default' && vt.toLowerCase() !== 'default title') {
      return vt;
    }
    return null;
  }
  return `${n} variants`;
}

function giftOutOfStock(g: GiftProduct): boolean {
  if (g.outOfStock === true) return true;
  const vs = g.selectedVariants || [];
  if (vs.length > 0) {
    return vs.every((v) => v.available === false);
  }
  return false;
}

function withGiftSubtitle(g: GiftProduct): GiftProduct {
  return {
    ...g,
    subtitle: giftSubtitle(g),
    outOfStock: giftOutOfStock(g),
  };
}

function regroupSavedGifts(
  gifts: Array<{
    id?: string;
    productId?: string;
    variantId?: string | null;
    title?: string;
    imageUrl?: string | null;
    quantity?: number;
    allowedVariantIds?: string[];
    selectedVariants?: Array<{ id: string; title: string; price?: string | null }>;
    totalVariants?: number;
  }>,
): GiftProduct[] {
  const byProduct = new Map<string, GiftProduct>();
  for (const g of gifts) {
    const pid = stripShopifyId(g.productId || g.id);
    if (!pid) continue;
    let productTitle = String(g.title || pid || 'Gift');
    const sep = productTitle.indexOf(' — ');
    if (sep > 0) productTitle = productTitle.slice(0, sep).trim();
    const allowedFromField = Array.isArray(g.allowedVariantIds)
      ? g.allowedVariantIds.map(stripShopifyId).filter(Boolean)
      : [];
    const fromSelected = Array.isArray(g.selectedVariants)
      ? g.selectedVariants
          .map((v) => ({
            id: stripShopifyId(v.id),
            title: v.title || 'Default',
            price: v.price ?? null,
          }))
          .filter((v) => v.id)
      : [];
    const singleVid = g.variantId != null ? stripShopifyId(g.variantId) : '';
    let variants: Array<{ id: string; title: string; price?: string | null }> =
      fromSelected.length
        ? fromSelected
        : allowedFromField.map((id) => ({
            id,
            title:
              singleVid === id && sep > 0
                ? String(g.title || '').slice(sep + 3).trim() || 'Default'
                : 'Default',
            price: null,
          }));
    if (!variants.length && singleVid) {
      variants = [
        {
          id: singleVid,
          title:
            sep > 0
              ? String(g.title || '').slice(sep + 3).trim() || 'Default'
              : 'Default',
          price: null,
        },
      ];
    }
    const existing = byProduct.get(pid);
    if (!existing) {
      byProduct.set(
        pid,
        withGiftSubtitle({
          id: pid,
          productId: pid,
          title: productTitle,
          imageUrl: g.imageUrl || null,
          quantity: Math.max(1, Number(g.quantity) || 1),
          selectedVariants: variants,
          totalVariants: g.totalVariants || undefined,
        }),
      );
    } else {
      const merged = new Map(
        (existing.selectedVariants || []).map((v) => [v.id, v]),
      );
      for (const v of variants) {
        if (!merged.has(v.id)) merged.set(v.id, v);
        else if (
          merged.get(v.id)!.title === 'Default' &&
          v.title !== 'Default'
        ) {
          merged.set(v.id, v);
        }
      }
      byProduct.set(
        pid,
        withGiftSubtitle({
          ...existing,
          title: existing.title || productTitle,
          imageUrl: existing.imageUrl || g.imageUrl || null,
          quantity: Math.max(
            existing.quantity || 1,
            Math.max(1, Number(g.quantity) || 1),
          ),
          selectedVariants: Array.from(merged.values()),
          totalVariants:
            Math.max(existing.totalVariants || 0, g.totalVariants || 0) ||
            existing.totalVariants,
        }),
      );
    }
  }
  return Array.from(byProduct.values());
}

interface Milestone {
  id: string;
  type: RewardType;
  threshold: string;
  discountType: 'percentage' | 'fixed';
  discountValue: string;
  giftProducts: GiftProduct[];
}

const TYPE_META: Record<
  RewardType,
  { label: string; icon: string; badge: string }
> = {
  order_discount: { label: 'Order discount', icon: '🏷️', badge: 'Order discount' },
  free_shipping: { label: 'Free shipping', icon: '🚚', badge: 'Free shipping' },
  free_gift: { label: 'Free gift', icon: '🎁', badge: 'Free gift' },
};

function newId() {
  return `ms-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function defaultMilestone(type: RewardType, threshold: string): Milestone {
  return {
    id: newId(),
    type,
    threshold,
    discountType: 'percentage',
    discountValue: '10',
    giftProducts: [],
  };
}

export function MilestoneRewardsWizard() {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const api = useApi();
  const { currency, moneyPrefix, formatMoney } = useStore();

  const campaignId =
    routeId && routeId !== 'new' && routeId !== 'configure' ? routeId : null;
  const isEdit = Boolean(campaignId);
  const [loadingCampaign, setLoadingCampaign] = useState(isEdit);

  const [mainStep, setMainStep] = useState<MainStep>(1);
  const [name, setName] = useState('Milestone rewards');
  const [schedule, setSchedule] = useState<CampaignScheduleValue>(defaultScheduleValue);
  const [tierBasis, setTierBasis] = useState<TierBasis>('cart_value');
  const [distribution, setDistribution] =
    useState<DistributionRule>('all_eligible');

  const [milestones, setMilestones] = useState<Milestone[]>([
    defaultMilestone('order_discount', '50'),
    defaultMilestone('free_shipping', '150'),
  ]);

  /** One discount config for the whole campaign (not per milestone) */
  const [discountCode, setDiscountCode] = useState(() => generateMilestoneCode());

  /** When switching tier basis, reset thresholds to sensible defaults */
  function setTierBasisAndThresholds(next: TierBasis) {
    setTierBasis(next);
    if (next === 'product_quantity') {
      setMilestones((prev) =>
        prev.map((m, i) => ({
          ...m,
          // Start at 2 items, then 4, 6, 8…
          threshold: String(2 + i * 2),
        })),
      );
      setPreviewProgress(40);
    } else {
      setMilestones((prev) =>
        prev.map((m, i) => ({
          ...m,
          threshold: String(50 + i * 100),
        })),
      );
    }
  }

  const [previewProgress, setPreviewProgress] = useState(40);
  const [accentColor, setAccentColor] = useState('#0D7A5F');

  /** Display settings — product page + auto-add only */
  const [congratsBars, setCongratsBars] = useState(true);
  const [congratsTitle, setCongratsTitle] = useState('Gift Locked In 🎁');
  const [congratsSubtitle, setCongratsSubtitle] = useState('Added to cart');
  const [celebrationEnabled, setCelebrationEnabled] = useState(true);
  const [celebrationMessage, setCelebrationMessage] = useState(
    'You have got {{nearest_reward}}',
  );
  /** Per-milestone progress titles & reward labels (keyed by milestone id) */
  const [milestoneTitles, setMilestoneTitles] = useState<Record<string, string>>(
    {},
  );
  const [rewardTexts, setRewardTexts] = useState<Record<string, string>>({});

  // Advanced
  const [priority, setPriority] = useState('10');
  const [stopOnMatch, setStopOnMatch] = useState(true);
  const [usageLimitEnabled, setUsageLimitEnabled] = useState(false);
  const [totalUses, setTotalUses] = useState('');
  const [maxUsesPerCustomer, setMaxUsesPerCustomer] = useState('1');
  type Eligibility = 'all' | 'segments' | 'utm' | 'location';
  const [eligibility, setEligibility] = useState<Eligibility>('all');
  const [segmentValue, setSegmentValue] = useState('');
  const [utmValue, setUtmValue] = useState('');
  const [locationCodes, setLocationCodes] = useState<string[]>([]);
  const [locationLabels, setLocationLabels] = useState<Record<string, string>>({});
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);

  const [giftBrowseFor, setGiftBrowseFor] = useState<string | null>(null);
  const [addMilestoneOpen, setAddMilestoneOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const embedded = isShopifyEmbedded();

  const formSnapshot = useMemo(
    () => ({
      name,
      schedule,
      tierBasis,
      distribution,
      milestones,
      discountCode,
      previewProgress,
      accentColor,
      congratsBars,
      congratsTitle,
      congratsSubtitle,
      celebrationEnabled,
      celebrationMessage,
      milestoneTitles,
      rewardTexts,
      priority,
      stopOnMatch,
      usageLimitEnabled,
      totalUses,
      maxUsesPerCustomer,
      eligibility,
      segmentValue,
      utmValue,
      locationCodes,
      locationLabels,
    }),
    [
      name,
      schedule,
      tierBasis,
      distribution,
      milestones,
      discountCode,
      previewProgress,
      accentColor,
      congratsBars,
      congratsTitle,
      congratsSubtitle,
      celebrationEnabled,
      celebrationMessage,
      milestoneTitles,
      rewardTexts,
      priority,
      stopOnMatch,
      usageLimitEnabled,
      totalUses,
      maxUsesPerCustomer,
      eligibility,
      segmentValue,
      utmValue,
      locationCodes,
      locationLabels,
    ],
  );
  const { dirty, markClean, getBaseline, markCleanAfterDiscard } =
    useUnsavedChanges(formSnapshot);

  useEffect(() => {
    if (loadingCampaign) return;
    markClean(formSnapshot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCampaign]);

  /** PDP product block (Freebi Milestone Progress) detection */
  const [productBlock, setProductBlock] = useState<ProductBlockStatus | null>(
    null,
  );
  const [productBlockLoading, setProductBlockLoading] = useState(false);
  const [productBlockBusy, setProductBlockBusy] = useState(false);

  const loadProductBlock = useCallback(async () => {
    setProductBlockLoading(true);
    try {
      const status = await api.get<ProductBlockStatus>(
        '/stores/theme/product-block',
      );
      setProductBlock(status);
    } catch {
      // Fallback deep link if API fails
      setProductBlock({
        active: false,
        statusLabel: 'Not added',
        blockName: 'Freebi Milestone Progress',
        blockHandle: 'progress-bar',
        message:
          'Could not verify product block status. Open the theme customizer to add it on the product page.',
        deepLink:
          'https://admin.shopify.com/themes/current/editor?template=product',
        source: 'unknown',
      });
    } finally {
      setProductBlockLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (mainStep === 2) {
      void loadProductBlock();
    }
  }, [mainStep, loadProductBlock]);

  // Re-check when merchant returns from theme customizer
  useEffect(() => {
    if (mainStep !== 2) return;
    const onFocus = () => void loadProductBlock();
    const onVis = () => {
      if (document.visibilityState === 'visible') void loadProductBlock();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [mainStep, loadProductBlock]);

  async function openProductBlockCustomizer() {
    if (!productBlock?.deepLink) return;
    setProductBlockBusy(true);
    try {
      window.open(productBlock.deepLink, '_blank', 'noopener,noreferrer');

      // Demo / local without real theme API — mark added so flow is testable
      const isDemo =
        productBlock.source === 'demo' ||
        productBlock.deepLink.includes('YOUR_CLIENT_ID');
      if (isDemo) {
        const updated = await api.patch<ProductBlockStatus>(
          '/stores/theme/product-block',
          { active: true },
        );
        setProductBlock(updated);
      } else {
        window.setTimeout(() => void loadProductBlock(), 4000);
      }
    } catch {
      // ignore
    } finally {
      setProductBlockBusy(false);
    }
  }

  const freeShippingUsed = milestones.some((m) => m.type === 'free_shipping');
  const canAddMilestone = milestones.length < 4;
  const canRemoveMilestone = milestones.length > 2;

  useEffect(() => {
    if (!campaignId) {
      setLoadingCampaign(false);
      return;
    }
    setLoadingCampaign(true);
    api
      .get<{
        name: string;
        priority?: number;
        startsAt?: string | null;
        endsAt?: string | null;
        maxUsesTotal?: number | null;
        maxUsesPerCustomer?: number | null;
        config?: Record<string, unknown>;
      }>(`/campaigns/${campaignId}`)
      .then((c) => {
        setName(c.name || 'Milestone rewards');
        setPriority(String(c.priority ?? 1));
        setSchedule(scheduleFromIso(c.startsAt, c.endsAt));

        const cfg = (c.config || {}) as Record<string, unknown>;
        const cg = (cfg.cartGoal || {}) as Record<string, unknown>;
        const display = (cfg.display || {}) as Record<string, unknown>;
        const stacking = (cfg.stacking || {}) as Record<string, unknown>;
        const advanced = (cfg.advanced || {}) as Record<string, unknown>;
        const discount = (cfg.discount || {}) as Record<string, unknown>;
        const elig = (advanced.customerEligibility || {}) as Record<
          string,
          unknown
        >;
        const celebration = (display.celebration || {}) as Record<
          string,
          unknown
        >;

        const advUsageOff = advanced.usageLimitEnabled === false;
        const advTotal =
          advanced.totalUses != null && Number(advanced.totalUses) > 0
            ? Number(advanced.totalUses)
            : null;
        const topTotal =
          c.maxUsesTotal != null && Number(c.maxUsesTotal) > 0
            ? Number(c.maxUsesTotal)
            : null;
        if (!advUsageOff && (advanced.usageLimitEnabled === true || topTotal || advTotal)) {
          setUsageLimitEnabled(true);
          setTotalUses(String(topTotal ?? advTotal ?? ''));
        } else {
          setUsageLimitEnabled(false);
          setTotalUses('');
        }
        const maxPer =
          advanced.maxUsesPerCustomer != null &&
          Number(advanced.maxUsesPerCustomer) > 0
            ? Number(advanced.maxUsesPerCustomer)
            : c.maxUsesPerCustomer != null && Number(c.maxUsesPerCustomer) > 0
              ? Number(c.maxUsesPerCustomer)
              : 1;
        setMaxUsesPerCustomer(String(Math.max(1, Math.floor(maxPer) || 1)));

        if (cg.basis === 'product_quantity' || cg.basis === 'cart_value') {
          setTierBasis(cg.basis);
        }
        if (cg.distribution === 'nearest_only' || cg.distribution === 'all_eligible') {
          setDistribution(cg.distribution);
        }
        if (discount.code) setDiscountCode(String(discount.code));
        if (typeof stacking.stopOnMatch === 'boolean') {
          setStopOnMatch(stacking.stopOnMatch);
        }
        if (stacking.priority != null) setPriority(String(stacking.priority));

        if (typeof celebration.enabled === 'boolean') {
          setCelebrationEnabled(celebration.enabled);
        }
        if (typeof celebration.message === 'string') {
          setCelebrationMessage(celebration.message);
        }
        if (typeof display.congratsBars === 'boolean') {
          setCongratsBars(display.congratsBars);
        }
        if (typeof display.congratsTitle === 'string') {
          setCongratsTitle(display.congratsTitle);
        }
        if (typeof display.congratsSubtitle === 'string') {
          setCongratsSubtitle(display.congratsSubtitle);
        }
        if (typeof display.accentColor === 'string') {
          setAccentColor(display.accentColor);
        }

        const msgs = (display.milestoneMessages || []) as Array<{
          milestoneId?: string;
          titleBefore?: string;
          rewardLabel?: string;
        }>;
        const titles: Record<string, string> = {};
        const rewards: Record<string, string> = {};
        for (const m of msgs) {
          if (m.milestoneId && m.titleBefore) titles[m.milestoneId] = m.titleBefore;
          if (m.milestoneId && m.rewardLabel) rewards[m.milestoneId] = m.rewardLabel;
        }
        setMilestoneTitles(titles);
        setRewardTexts(rewards);

        const rawMs = (cg.milestones || []) as Array<Record<string, unknown>>;
        if (rawMs.length >= 2) {
          setMilestones(
            rawMs.map((m) => {
              const rewardType = String(
                m.rewardType || m.type || 'order_discount',
              );
              let type: RewardType = 'order_discount';
              if (
                rewardType.includes('ship') ||
                rewardType === 'free_shipping'
              ) {
                type = 'free_shipping';
              } else if (
                rewardType.includes('gift') ||
                rewardType === 'free_gift'
              ) {
                type = 'free_gift';
              } else {
                type = 'order_discount';
              }
              const gifts = (m.gifts || m.giftProducts || []) as GiftProduct[];
              return {
                id: String(m.id || newId()),
                type,
                threshold: String(m.threshold ?? ''),
                discountType:
                  m.discountType === 'fixed' ? 'fixed' : 'percentage',
                discountValue: String(m.value ?? m.discountValue ?? '10'),
                giftProducts: Array.isArray(gifts)
                  ? regroupSavedGifts(
                      gifts as Array<{
                        id?: string;
                        productId?: string;
                        variantId?: string | null;
                        title?: string;
                        imageUrl?: string | null;
                        quantity?: number;
                        allowedVariantIds?: string[];
                        selectedVariants?: Array<{
                          id: string;
                          title: string;
                          price?: string | null;
                        }>;
                        totalVariants?: number;
                      }>,
                    )
                  : [],
              };
            }),
          );
        }

        if (elig.type === 'segments' || elig.type === 'utm' || elig.type === 'location') {
          setEligibility(elig.type);
        } else {
          setEligibility('all');
        }
        setSegmentValue(segmentValueFromEligibility(elig));
        setUtmValue(typeof elig.utm === 'string' ? elig.utm : '');
        if (Array.isArray(elig.locationCodes)) {
          setLocationCodes(elig.locationCodes as string[]);
        } else {
          setLocationCodes([]);
        }
        if (Array.isArray(elig.locations)) {
          const labels: Record<string, string> = {};
          for (const loc of elig.locations as Array<{ code: string; name?: string }>) {
            labels[loc.code] = loc.name || loc.code;
          }
          setLocationLabels(labels);
        } else {
          setLocationLabels({});
        }
      })
      .catch((e: Error) => setError(e.message || 'Failed to load campaign'))
      .finally(() => setLoadingCampaign(false));
  }, [api, campaignId]);

  const sortedForPreview = useMemo(
    () =>
      [...milestones].sort(
        (a, b) => (Number(a.threshold) || 0) - (Number(b.threshold) || 0),
      ),
    [milestones],
  );

  const maxThreshold = Math.max(
    ...sortedForPreview.map((m) => Number(m.threshold) || 0),
    1,
  );
  const simulatedCart = (previewProgress / 100) * maxThreshold;
  const nextMilestone = sortedForPreview.find(
    (m) => simulatedCart < (Number(m.threshold) || 0),
  );
  const remaining = nextMilestone
    ? Math.max(0, (Number(nextMilestone.threshold) || 0) - simulatedCart)
    : 0;

  function updateMilestone(id: string, patch: Partial<Milestone>) {
    setMilestones((prev) =>
      prev.map((m) => (m.id === id ? { ...m, ...patch } : m)),
    );
  }

  function changeMilestoneType(id: string, type: RewardType) {
    if (type === 'free_shipping' && freeShippingUsed) {
      const current = milestones.find((m) => m.id === id);
      if (current?.type !== 'free_shipping') return;
    }
    updateMilestone(id, { type });
    // Reset reward display label to the new type’s default
    setRewardTexts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  function addMilestoneOfType(type: RewardType) {
    if (!canAddMilestone) return;
    if (type === 'free_shipping' && freeShippingUsed) return;
    const last = milestones[milestones.length - 1];
    const step = tierBasis === 'product_quantity' ? 2 : 100;
    const base =
      Number(last?.threshold) || (tierBasis === 'product_quantity' ? 2 : 50);
    const nextThreshold = String(base + step);
    setMilestones((prev) => [...prev, defaultMilestone(type, nextThreshold)]);
    setAddMilestoneOpen(false);
  }

  function removeMilestone(id: string) {
    if (!canRemoveMilestone) return;
    setMilestones((prev) => prev.filter((m) => m.id !== id));
  }

  /** Free shipping only once — omit from other milestones when already used */
  function selectableTypes(m: Milestone): Array<{ label: string; value: string }> {
    const opts: Array<{ label: string; value: string }> = [
      { label: 'Order discount', value: 'order_discount' },
      { label: 'Free gift', value: 'free_gift' },
    ];
    if (!freeShippingUsed || m.type === 'free_shipping') {
      opts.splice(1, 0, { label: 'Free shipping', value: 'free_shipping' });
    }
    return opts;
  }

  function toggleGiftFromPicker(item: CatalogItem) {
    if (!giftBrowseFor) return;
    const milestoneId = giftBrowseFor;
    const productId =
      stripShopifyId(
        item.productId ||
          (item.kind === 'product' ? item.id : undefined) ||
          '',
      ) || stripShopifyId(item.id);
    if (!productId) return;

    const productTitle = productTitleFromCatalog(item);
    const variantId = stripShopifyId(
      item.variantId || (item.kind === 'variant' ? item.id : '') || '',
    );
    const totalFromItem =
      item.totalVariants ||
      (item.variants && item.variants.length
        ? item.variants.length
        : undefined);

    setMilestones((prev) =>
      prev.map((m) => {
        if (m.id !== milestoneId) return m;
        const gifts = m.giftProducts;
        const idx = gifts.findIndex(
          (g) =>
            stripShopifyId(g.productId || g.id) === productId ||
            g.id === productId,
        );
        const existing = idx >= 0 ? gifts[idx] : null;

        if (!variantId) {
          if (existing) {
            return {
              ...m,
              giftProducts: gifts.filter((_, i) => i !== idx),
            };
          }
          return {
            ...m,
            giftProducts: [
              ...gifts,
              withGiftSubtitle({
                id: productId,
                productId,
                title: productTitle,
                imageUrl: item.imageUrl || null,
                quantity: 1,
                selectedVariants: [],
                totalVariants: totalFromItem,
                outOfStock: item.available === false,
              }),
            ],
          };
        }

        const vEntry = {
          id: variantId,
          title: variantTitleFromCatalog(item),
          price: item.price ?? null,
          available: item.available !== false,
        };

        if (!existing) {
          return {
            ...m,
            giftProducts: [
              ...gifts,
              withGiftSubtitle({
                id: productId,
                productId,
                title: productTitle,
                imageUrl: item.imageUrl || null,
                quantity: 1,
                selectedVariants: [vEntry],
                totalVariants: totalFromItem,
              }),
            ],
          };
        }

        const cur = existing.selectedVariants || [];
        const has = cur.some((v) => stripShopifyId(v.id) === variantId);
        let nextVariants: typeof cur;
        if (has) {
          nextVariants = cur.filter((v) => stripShopifyId(v.id) !== variantId);
          if (nextVariants.length === 0) {
            return {
              ...m,
              giftProducts: gifts.filter((_, i) => i !== idx),
            };
          }
        } else {
          nextVariants = [...cur, vEntry];
        }

        const nextGifts = [...gifts];
        nextGifts[idx] = withGiftSubtitle({
          ...existing,
          title: productTitle || existing.title,
          imageUrl: existing.imageUrl || item.imageUrl || null,
          selectedVariants: nextVariants,
          totalVariants:
            Math.max(existing.totalVariants || 0, totalFromItem || 0) ||
            existing.totalVariants ||
            totalFromItem,
        });
        return { ...m, giftProducts: nextGifts };
      }),
    );
  }

  function rewardLabel(m: Milestone): string {
    if (m.type === 'order_discount') {
      return m.discountType === 'percentage'
        ? `${m.discountValue}% OFF`
        : `${moneyPrefix}${m.discountValue} OFF`;
    }
    if (m.type === 'free_shipping') return 'Free shipping';
    return m.giftProducts.length
      ? `Free gift (${m.giftProducts.length})`
      : 'Free gift';
  }

  function defaultRewardText(m: Milestone): string {
    if (m.type === 'order_discount') return '{{discount}} OFF';
    if (m.type === 'free_shipping') return 'Free shipping';
    return 'Free gift';
  }

  function defaultMilestoneTitle(): string {
    return 'You are {{amount_left}} away from getting {{nearest_reward}}';
  }

  function getMilestoneTitle(m: Milestone): string {
    return milestoneTitles[m.id] ?? defaultMilestoneTitle();
  }

  function getRewardText(m: Milestone): string {
    return rewardTexts[m.id] ?? defaultRewardText(m);
  }

  function setMilestoneTitle(id: string, value: string) {
    setMilestoneTitles((prev) => ({ ...prev, [id]: value }));
  }

  function setRewardText(id: string, value: string) {
    setRewardTexts((prev) => ({ ...prev, [id]: value }));
  }

  function fillRewardOnly(tpl: string, m: Milestone): string {
    const discount =
      m.type === 'order_discount'
        ? m.discountType === 'percentage'
          ? `${m.discountValue}%`
          : `${moneyPrefix}${m.discountValue}`
        : '';
    return tpl
      .replace(/\{\{discount\}\}/g, discount)
      .replace(/\{\{nearest_reward\}\}/g, rewardLabel(m));
  }

  /** Resolve {{variables}} for preview / storefront templates */
  function fillTemplate(tpl: string, m?: Milestone): string {
    const amount =
      tierBasis === 'cart_value'
        ? formatMoney(remaining)
        : `${Math.ceil(remaining)} item(s)`;
    const nearest = m ? fillRewardOnly(getRewardText(m), m) : '';
    const discount = m
      ? m.type === 'order_discount'
        ? m.discountType === 'percentage'
          ? `${m.discountValue}%`
          : `${moneyPrefix}${m.discountValue}`
        : ''
      : '';
    return tpl
      .replace(/\{\{amount_left\}\}/g, amount)
      .replace(/\{amount\}/g, amount)
      .replace(/\{\{nearest_reward\}\}/g, nearest)
      .replace(/\{\{discount\}\}/g, discount);
  }

  function thresholdDisplay(m: Milestone): string {
    if (tierBasis === 'product_quantity') {
      return `${m.threshold} item${m.threshold === '1' ? '' : 's'}`;
    }
    return formatMoney(Number(m.threshold) || 0);
  }

  function validateBeforeSave(): { message: string; step: MainStep } | null {
    if (!name.trim()) {
      return { message: 'Enter a campaign name before saving.', step: 1 };
    }
    if (milestones.length < 2 || milestones.length > 4) {
      return { message: 'Add between 2 and 4 milestones before saving.', step: 1 };
    }
    const shippingCount = milestones.filter((m) => m.type === 'free_shipping').length;
    if (shippingCount > 1) {
      return { message: 'Free shipping can only be used once.', step: 1 };
    }
    for (let i = 0; i < milestones.length; i++) {
      const m = milestones[i];
      if (!m.threshold || Number(m.threshold) <= 0) {
        return {
          message: `Milestone ${i + 1}: set a threshold greater than 0.`,
          step: 1,
        };
      }
      if (m.type === 'order_discount' && (m.discountValue === '' || Number(m.discountValue) < 0)) {
        return {
          message: `Milestone ${i + 1}: set a valid discount value.`,
          step: 1,
        };
      }
      if (m.type === 'free_gift' && (!m.giftProducts || m.giftProducts.length === 0)) {
        return {
          message: `Milestone ${i + 1}: add at least one free gift product.`,
          step: 1,
        };
      }
    }
    // Thresholds should increase with each step
    const sorted = [...milestones].sort(
      (a, b) => Number(a.threshold) - Number(b.threshold),
    );
    for (let i = 1; i < sorted.length; i++) {
      if (Number(sorted[i].threshold) <= Number(sorted[i - 1].threshold)) {
        return {
          message: 'Each milestone threshold must be higher than the previous one.',
          step: 1,
        };
      }
    }
    if (!discountCode.trim()) {
      return { message: 'Enter a discount code for milestone rewards.', step: 1 };
    }
    if (eligibility === 'segments' && !segmentIdsFromValue(segmentValue).length) {
      return {
        message: 'Select at least one customer segment, or set eligibility to all customers.',
        step: 1,
      };
    }
    if (eligibility === 'utm' && !utmValue.trim()) {
      return {
        message: 'Enter a UTM value, or set eligibility to all customers.',
        step: 1,
      };
    }
    if (eligibility === 'location' && locationCodes.length === 0) {
      return {
        message: 'Select at least one location, or set eligibility to all customers.',
        step: 1,
      };
    }
    if (usageLimitEnabled && (!totalUses || Number(totalUses) < 1)) {
      return {
        message: 'Enter a total usage limit greater than 0, or turn off the usage limit.',
        step: 1,
      };
    }
    return null;
  }

  async function save(activate: boolean) {
    const invalid = validateBeforeSave();
    if (invalid) {
      setMainStep(invalid.step);
      setError(invalid.message);
      setMessage(null);
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const uniqueCode = await resolveUniqueDiscountCode(
        api,
        'MILESTONE_',
        discountCode,
        campaignId || undefined,
      );
      if (uniqueCode !== discountCode.trim().toUpperCase()) {
        setDiscountCode(uniqueCode);
      }

      const config = {
        cartGoal: {
          basis: tierBasis,
          distribution,
          currency,
          showConfetti: true,
          animateProgress: true,
          progressBarStyle: 'stepped',
          milestones: milestones.map((m, index) => ({
            id: m.id,
            index: index + 1,
            threshold: Number(m.threshold) || 0,
            type:
              m.type === 'order_discount'
                ? 'discount_percent'
                : m.type === 'free_shipping'
                  ? 'free_shipping'
                  : 'free_gift',
            rewardType: m.type,
            label: rewardLabel(m),
            discountType: m.discountType,
            value: Number(m.discountValue) || 0,
            gifts: m.giftProducts.map((g) => {
              const pid =
                stripShopifyId(g.productId || g.id) || g.productId || g.id;
              const vids = (g.selectedVariants || [])
                .map((v) => stripShopifyId(v.id))
                .filter(Boolean);
              const singleVid = vids.length === 1 ? vids[0] : null;
              return {
                id: pid,
                productId: pid,
                variantId: singleVid,
                allowedVariantIds: vids.length ? vids : undefined,
                selectedVariants: g.selectedVariants || [],
                totalVariants: g.totalVariants,
                title: g.title,
                imageUrl: g.imageUrl || null,
                quantity: Math.max(1, Number(g.quantity) || 1),
              };
            }),
          })),
        },
        // Shopify codes created on save (order / shipping / gift BXGY)
        discount: {
          code: uniqueCode,
          // type/value are per-milestone for order rewards; gift codes are 100% free
          type: 'percentage',
          value: 100,
        },
        display: {
          method: 'milestone_reward',
          location: 'product_page',
          giftMethod: 'automatic',
          congratsBars,
          congratsTitle: congratsTitle.trim() || 'Gift Locked In 🎁',
          congratsSubtitle: congratsSubtitle.trim() || 'Added to cart',
          accentColor,
          celebration: {
            enabled: celebrationEnabled,
            message: celebrationMessage,
          },
          milestoneMessages: milestones.map((m, index) => ({
            milestoneId: m.id,
            index: index + 1,
            titleBefore: getMilestoneTitle(m),
            rewardLabel: getRewardText(m),
          })),
        },
        stacking: {
          priority: Math.max(0, Math.floor(Number(priority) || 0)),
          stopOnMatch,
        },
        advanced: {
          usageLimitEnabled,
          totalUses:
            usageLimitEnabled && totalUses.trim()
              ? Math.max(1, Math.floor(Number(totalUses)) || 1)
              : null,
          maxUsesPerCustomer: Math.max(
            1,
            Math.floor(Number(maxUsesPerCustomer)) || 1,
          ),
          customerEligibility: {
            type: eligibility,
            segments:
              eligibility === 'segments'
                ? segmentNamesFromValue(segmentValue)
                : [],
            segmentIds:
              eligibility === 'segments'
                ? segmentIdsFromValue(segmentValue)
                : [],
            utm: eligibility === 'utm' ? utmValue.trim() : null,
            locationCodes: eligibility === 'location' ? locationCodes : [],
            locations:
              eligibility === 'location'
                ? locationCodes.map((code) => ({
                    code,
                    name: locationLabels[code] || code,
                  }))
                : [],
          },
        },
      };

      const { startsAt, endsAt } = scheduleToIso(schedule);
      const payload = {
        name: name || 'Milestone rewards',
        description: `Tiered rewards · ${milestones.length} milestones · ${tierBasis === 'cart_value' ? 'cart value' : 'quantity'}`,
        type: 'CART_GOAL' as const,
        priority: Math.max(0, Math.floor(Number(priority) || 0)),
        status: 'ACTIVE',
        startsAt: startsAt || null,
        endsAt: endsAt || null,
        config,
        tags: ['milestone_rewards'],
        maxUsesTotal:
          usageLimitEnabled && totalUses.trim()
            ? Math.max(1, Math.floor(Number(totalUses)) || 1)
            : null,
        maxUsesPerCustomer: Math.max(
          1,
          Math.floor(Number(maxUsesPerCustomer)) || 1,
        ),
      };

      if (campaignId) {
        await api.patch(`/campaigns/${campaignId}`, payload);
        markClean(formSnapshot);
        setMessage(t("Campaign saved"));
        showAppToast(t("Campaign saved"));
      } else {
        const created = await api.post<{ id: string }>('/campaigns', payload);
        markClean(formSnapshot);
        setMessage(t("Campaign saved"));
        showAppToast(t("Campaign saved"));
        navigate(`/campaigns/${created.id}?preset=milestone_rewards`);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setError(msg);
      showAppToast(msg, { isError: true });
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    const b = getBaseline();
    if (!b) return;
    setName(b.name);
    setSchedule(b.schedule);
    setTierBasis(b.tierBasis);
    setDistribution(b.distribution);
    setMilestones(b.milestones);
    setDiscountCode(b.discountCode);
    setPreviewProgress(b.previewProgress);
    setAccentColor(b.accentColor);
    setCongratsBars(b.congratsBars);
    setCongratsTitle(b.congratsTitle || 'Gift Locked In 🎁');
    setCongratsSubtitle(b.congratsSubtitle || 'Added to cart');
    setCelebrationEnabled(b.celebrationEnabled);
    setCelebrationMessage(b.celebrationMessage);
    setMilestoneTitles(b.milestoneTitles);
    setRewardTexts(b.rewardTexts);
    setPriority(b.priority);
    setStopOnMatch(b.stopOnMatch);
    setUsageLimitEnabled(b.usageLimitEnabled);
    setTotalUses(b.totalUses);
    setMaxUsesPerCustomer(b.maxUsesPerCustomer);
    setEligibility(b.eligibility);
    setSegmentValue(b.segmentValue);
    setUtmValue(b.utmValue);
    setLocationCodes(b.locationCodes);
    setLocationLabels(b.locationLabels);
    setError(null);
    setMessage(null);
    markCleanAfterDiscard(b);
  }

  function renderMilestones() {
    return (
      <BlockStack gap="400">
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              {t("Tiers & rewards")}
            </Text>

            <Text as="h3" variant="headingSm">
              {t("Tiered reward type")}
            </Text>
            <InlineStack gap="400" wrap>
              <RadioButton
                label={t("Total cart value")}
                checked={tierBasis === 'cart_value'}
                id="tier-cart"
                name="tierBasis"
                onChange={() => setTierBasisAndThresholds('cart_value')}
              />
              <RadioButton
                label={t("Product quantity")}
                checked={tierBasis === 'product_quantity'}
                id="tier-qty"
                name="tierBasis"
                onChange={() => setTierBasisAndThresholds('product_quantity')}
              />
            </InlineStack>
            <Text as="p" tone="subdued" variant="bodySm">
              {tierBasis === 'cart_value'
                ? `Thresholds use your store currency (${currency}).`
                : 'Thresholds are based on number of items in the cart.'}{' '}
              {t("Between 2 and 4 milestones. Free shipping can only be used once.")}
            </Text>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              {t("Milestones")}
            </Text>

            {milestones.map((m, index) => {
              const meta = TYPE_META[m.type];
              return (
                <Box
                  key={m.id}
                  padding="300"
                  borderRadius="200"
                  borderWidth="025"
                  borderColor="border"
                >
                  <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center" wrap>
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="span" fontWeight="semibold">
                          {t("Milestone #")}{index + 1}
                        </Text>
                        <Badge>{`${meta.icon} ${meta.badge}`}</Badge>
                      </InlineStack>
                      <Button
                        variant="plain"
                        tone="critical"
                        disabled={!canRemoveMilestone}
                        onClick={() => removeMilestone(m.id)}
                      >
                        {t("Remove")}
                      </Button>
                    </InlineStack>

                    <Select
                      label={t("Reward type")}
                      options={selectableTypes(m)}
                      value={m.type}
                      onChange={(v) => changeMilestoneType(m.id, v as RewardType)}
                    />
                    {freeShippingUsed && m.type !== 'free_shipping' && (
                      <Text as="p" tone="subdued" variant="bodySm">
                        {t("Free shipping is already used on another milestone.")}
                      </Text>
                    )}

                    <TextField
                      label={t("Buy at least")}
                      type="number"
                      value={m.threshold}
                      onChange={(v) => updateMilestone(m.id, { threshold: v })}
                      prefix={tierBasis === 'cart_value' ? moneyPrefix : undefined}
                      suffix={tierBasis === 'product_quantity' ? 'items' : undefined}
                      autoComplete="off"
                      requiredIndicator
                    />

                    {m.type === 'order_discount' && (
                      <InlineStack gap="300" wrap>
                        <div style={{ flex: 1, minWidth: 140 }}>
                          <Select
                            label={t("Discount type")}
                            options={[
                              { label: t("Percentage"), value: 'percentage' },
                              { label: t("Fixed amount"), value: 'fixed' },
                            ]}
                            value={m.discountType}
                            onChange={(v) =>
                              updateMilestone(m.id, {
                                discountType: v as 'percentage' | 'fixed',
                              })
                            }
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 120 }}>
                          <TextField
                            label={t("Value")}
                            type="number"
                            value={m.discountValue}
                            onChange={(v) =>
                              updateMilestone(m.id, { discountValue: v })
                            }
                            prefix={
                              m.discountType === 'fixed' ? moneyPrefix : undefined
                            }
                            suffix={
                              m.discountType === 'percentage' ? '%' : undefined
                            }
                            autoComplete="off"
                          />
                        </div>
                      </InlineStack>
                    )}

                    {m.type === 'free_gift' && (
                      <BlockStack gap="200">
                        <InlineStack gap="200" blockAlign="center" wrap>
                          <Text as="span" tone="subdued">
                            {m.giftProducts.length === 0
                              ? 'No products selected'
                              : `${m.giftProducts.length} gift product(s)`}
                          </Text>
                          <Button onClick={() => setGiftBrowseFor(m.id)}>
                            {t("Select products")}
                          </Button>
                        </InlineStack>
                        {m.giftProducts.map((g) => (
                          <SelectedProductRow
                            key={g.productId || g.id}
                            product={{
                              ...g,
                              subtitle: g.subtitle ?? giftSubtitle(g),
                              outOfStock: giftOutOfStock(g),
                            }}
                            showQuantity
                            onQuantityChange={(quantity) =>
                              updateMilestone(m.id, {
                                giftProducts: m.giftProducts.map((x) =>
                                  (x.productId || x.id) ===
                                  (g.productId || g.id)
                                    ? { ...x, quantity }
                                    : x,
                                ),
                              })
                            }
                            onRemove={() =>
                              updateMilestone(m.id, {
                                giftProducts: m.giftProducts.filter(
                                  (x) =>
                                    (x.productId || x.id) !==
                                    (g.productId || g.id),
                                ),
                              })
                            }
                            onEdit={() => setGiftBrowseFor(m.id)}
                          />
                        ))}
                      </BlockStack>
                    )}
                  </BlockStack>
                </Box>
              );
            })}

            <Button
              onClick={() => setAddMilestoneOpen(true)}
              disabled={!canAddMilestone}
            >
              {t("+ Add milestone")}
            </Button>
            {!canAddMilestone && (
              <Text as="p" tone="subdued" variant="bodySm">
                {t("Maximum 4 milestones.")}
              </Text>
            )}
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              {t("Discount config")}
            </Text>
            <Text as="p" tone="subdued" variant="bodySm">
              {t("One discount for this campaign (used when syncing to Shopify Discounts).")}
            </Text>
            <TextField
              label={t("Discount code")}
              value={discountCode}
              onChange={(v) => setDiscountCode(v.toUpperCase().replace(/\s+/g, ''))}
              autoComplete="off"
              helpText={t("Auto-generated MILESTONE_XXXXX. Unique across campaigns.")}
              placeholder="MILESTONE_A3K9M"
            />
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              {t("Reward distribution rule")}
            </Text>
            <Text as="p" tone="subdued" variant="bodySm">
              {t("Choose how rewards are delivered when customers qualify for multiple\n              milestones.")}
            </Text>
            <RadioButton
              label={t("Nearest milestone gift only")}
              helpText={t("Customers receive only the reward from the highest tier they've reached.")}
              checked={distribution === 'nearest_only'}
              id="dist-nearest"
              name="distribution"
              onChange={() => setDistribution('nearest_only')}
            />
            <RadioButton
              label={t("All eligible gifts")}
              helpText={t("Customers receive all rewards they've qualified for.")}
              checked={distribution === 'all_eligible'}
              id="dist-all"
              name="distribution"
              onChange={() => setDistribution('all_eligible')}
            />
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              {t("Advanced settings")}
            </Text>

            <Box padding="300" background="bg-surface-secondary" borderRadius="200">
              <BlockStack gap="300">
                <Text as="h3" variant="headingSm">
                  {t("Priority & stacking")}
                </Text>
                <TextField
                  label={t("Priority")}
                  type="number"
                  value={priority}
                  onChange={setPriority}
                  autoComplete="off"
                  helpText={t("Higher number wins when multiple campaigns unlock. Only the winner’s free gift and coupon apply.")}
                />
                <Checkbox
                  label={t("Don't combine with other Freebi offers")}
                  checked={stopOnMatch}
                  onChange={setStopOnMatch}
                  helpText={t("When this campaign unlocks, lower-priority Freebi campaigns won't add gifts or apply their coupon. Turn off only if you intentionally want Freebi offers to stack.")}
                />
              </BlockStack>
            </Box>

            <Box padding="300" background="bg-surface-secondary" borderRadius="200">
              <BlockStack gap="300">
                <Text as="h3" variant="headingSm">
                  {t("Usage limit")}
                </Text>
                <Checkbox
                  label={t("Limit total uses")}
                  checked={usageLimitEnabled}
                  onChange={setUsageLimitEnabled}
                  helpText={t("When enabled, this campaign stops after the total use count is reached.")}
                />
                <TextField
                  label={t("Total uses")}
                  type="number"
                  value={totalUses}
                  onChange={setTotalUses}
                  autoComplete="off"
                  disabled={!usageLimitEnabled}
                  placeholder={t("e.g. 1000")}
                  helpText={
                    usageLimitEnabled
                      ? 'Maximum times this offer can be redeemed across all customers.'
                      : 'Turn on the toggle above to set a total use limit.'
                  }
                />
                <TextField
                  label={t("Max uses per customer")}
                  type="number"
                  min={1}
                  value={maxUsesPerCustomer}
                  onChange={(v) => {
                    if (v === '') {
                      setMaxUsesPerCustomer('');
                      return;
                    }
                    const n = Math.floor(Number(v));
                    setMaxUsesPerCustomer(
                      Number.isFinite(n) && n >= 1 ? String(n) : '1',
                    );
                  }}
                  autoComplete="off"
                  helpText={t("How many times each customer can use this offer (default 1 = once).")}
                />
              </BlockStack>
            </Box>

            <Box padding="300" background="bg-surface-secondary" borderRadius="200">
              <BlockStack gap="300">
                <Text as="h3" variant="headingSm">
                  {t("Customer eligibility")}
                </Text>
                <Text as="p" tone="subdued" variant="bodySm">
                  {t("Who can receive these milestone rewards?")}
                </Text>
                {(
                  [
                    ['all', t('All customers')],
                    ['segments', t('Specific customer segments')],
                    ['utm', t('UTM link')],
                    ['location', t('Customer location')],
                  ] as const
                ).map(([value, label]) => (
                  <RadioButton
                    key={value}
                    label={label}
                    checked={eligibility === value}
                    id={`ms-elig-${value}`}
                    name="ms-eligibility"
                    onChange={() => setEligibility(value)}
                  />
                ))}
                {eligibility === 'segments' && (
                  <CustomerSegmentField
                    value={segmentValue}
                    onChange={setSegmentValue}
                    helpText={t("Type a segment name to search your Shopify customer segments.")}
                  />
                )}
                {eligibility === 'utm' && (
                  <TextField
                    label={t("UTM parameter")}
                    value={utmValue}
                    onChange={setUtmValue}
                    autoComplete="off"
                    placeholder={t("utm_campaign=milestones")}
                    helpText={t("Only customers arriving with this UTM value qualify.")}
                  />
                )}
                {eligibility === 'location' && (
                  <BlockStack gap="200">
                    <InlineStack gap="200" blockAlign="center" wrap>
                      <Text as="span" tone="subdued">
                        {locationCodes.length === 0
                          ? 'No locations selected'
                          : `${locationCodes.length} location${locationCodes.length === 1 ? '' : 's'} selected`}
                      </Text>
                      <Button onClick={() => setLocationPickerOpen(true)}>
                        {locationCodes.length ? 'Edit locations' : 'Browse locations'}
                      </Button>
                    </InlineStack>
                    {locationCodes.length > 0 && (
                      <InlineStack gap="100" wrap>
                        {locationCodes.map((code) => (
                          <Tag
                            key={code}
                            onRemove={() => {
                              setLocationCodes((prev) =>
                                prev.filter((c) => c !== code),
                              );
                              setLocationLabels((prev) => {
                                const next = { ...prev };
                                delete next[code];
                                return next;
                              });
                            }}
                          >
                            {locationLabels[code] || code}
                          </Tag>
                        ))}
                      </InlineStack>
                    )}
                  </BlockStack>
                )}
              </BlockStack>
            </Box>

            <CampaignScheduleFields value={schedule} onChange={setSchedule} />
          </BlockStack>
        </Card>
      </BlockStack>
    );
  }

  function renderDisplay() {
    const previewTitle = nextMilestone
      ? fillTemplate(getMilestoneTitle(nextMilestone), nextMilestone)
      : celebrationEnabled
        ? fillTemplate(
            celebrationMessage,
            sortedForPreview[sortedForPreview.length - 1],
          )
        : 'All milestones unlocked!';

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 300px',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <BlockStack gap="400">
          {/* Milestone display location — product page only */}
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                {t("Milestone display location")}
              </Text>
              <Checkbox
                label={t("Product page")}
                checked
                disabled
                helpText={t("Freebi Milestone Progress block on the product template (PDP).")}
              />

              <InlineStack gap="200" blockAlign="center" wrap>
                <Text as="span" tone="subdued" variant="bodySm">
                  {t("Product block status:")}
                </Text>
                {productBlockLoading && !productBlock ? (
                  <Badge>{t("Checking…")}</Badge>
                ) : (
                  <Badge
                    tone={productBlock?.active ? 'success' : 'attention'}
                  >
                    {productBlock?.statusLabel || 'Not added'}
                  </Badge>
                )}
                {productBlock && (
                  <Button
                    variant="plain"
                    loading={productBlockLoading}
                    onClick={() => void loadProductBlock()}
                  >
                    {t("Refresh")}
                  </Button>
                )}
              </InlineStack>

              {productBlock?.active ? (
                <Banner tone="success">
                  <p>
                    {productBlock.message ||
                      'Product block is on your product page.'}
                  </p>
                </Banner>
              ) : (
                <Banner tone="warning">
                  <BlockStack gap="200">
                    <p>
                      {productBlock?.message ||
                        'Product block is not on your product page yet. Customers won’t see milestone progress until you add it.'}
                    </p>
                    <p>
                      {t("Opens the theme customizer on the")}{' '}
                      <Text as="span" fontWeight="semibold">
                        {t("product template")}
                      </Text>{' '}
                      with{' '}
                      <Text as="span" fontWeight="semibold">
                        {t("Freebi Milestone Progress")}
                      </Text>{' '}
                      {t("ready to add. Click")}{' '}
                      <Text as="span" fontWeight="semibold">
                        {t("Save")}
                      </Text>{' '}
                      {t("in the customizer, then return here.")}
                    </p>
                    <InlineStack gap="300" blockAlign="center" wrap>
                      <Button
                        variant="primary"
                        loading={productBlockBusy}
                        onClick={() => void openProductBlockCustomizer()}
                      >
                        {t("Add product block in theme customizer")}
                      </Button>
                      {productBlock?.deepLink && (
                        <Link
                          url={productBlock.deepLink}
                          target="_blank"
                          removeUnderline={false}
                        >
                          {t("Open customizer link")}
                        </Link>
                      )}
                    </InlineStack>
                  </BlockStack>
                </Banner>
              )}
            </BlockStack>
          </Card>

          {/* Free gift — auto-add only */}
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                {t("Free gift selection method")}
              </Text>
              <RadioButton
                label={t("Auto-add gift to cart")}
                helpText={t("Gift will be automatically added when a milestone is reached.")}
                checked
                id="ms-gift-auto"
                name="ms-gift-method"
                onChange={() => undefined}
              />
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                {t("Cart Success Notification Bar")}
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                {t("Toast when a free gift is added to cart. Global colors and\n                auto-close live under Widgets → Cart Success Notification Bar.")}
              </Text>
              <Checkbox
                label={t("Show when gift is added to cart")}
                checked={congratsBars}
                onChange={setCongratsBars}
              />
              {congratsBars && (
                <>
                  <TextField
                    label={t("Title")}
                    value={congratsTitle}
                    onChange={setCongratsTitle}
                    autoComplete="off"
                    placeholder={t("Gift Locked In 🎁")}
                  />
                  <TextField
                    label={t("Subtitle")}
                    value={congratsSubtitle}
                    onChange={setCongratsSubtitle}
                    autoComplete="off"
                    placeholder={t("Added to cart")}
                  />
                  <Button
                    onClick={() =>
                      navigate(
                        `/widgets/congrats?return=${encodeURIComponent(
                          window.location.pathname + window.location.search,
                        )}`,
                      )
                    }
                  >
                    {t("Edit Cart Success Notification Bar style")}
                  </Button>
                </>
              )}
            </BlockStack>
          </Card>

          {/* Setting widget */}
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  {t("Setting widget")}
                </Text>
                <Button
                  variant="plain"
                  onClick={() =>
                    navigate(
                      `/widgets/milestone?return=${encodeURIComponent(
                        window.location.pathname + window.location.search,
                      )}`,
                    )
                  }
                >
                  {t("Edit widget")}
                </Button>
              </InlineStack>
              <Box
                padding="300"
                background="bg-surface-secondary"
                borderRadius="200"
              >
                <InlineStack align="space-between" blockAlign="center" wrap>
                  <BlockStack gap="100">
                    <Text as="span" fontWeight="semibold">
                      {t("Milestone progress")}
                    </Text>
                    <Text as="span" tone="subdued" variant="bodySm">
                      {t("Product-page progress bar with tier details — same design\n                      as Widgets → Milestone progress")}
                    </Text>
                  </BlockStack>
                  <Button
                    onClick={() =>
                      navigate(
                        `/widgets/milestone?return=${encodeURIComponent(
                          window.location.pathname + window.location.search,
                        )}`,
                      )
                    }
                  >
                    {t("Edit widget")}
                  </Button>
                </InlineStack>
              </Box>
            </BlockStack>
          </Card>

          {/* Celebration messages — one set of fields per milestone */}
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">
                    {t("Celebration message")}
                  </Text>
                  <Text as="p" tone="subdued" variant="bodySm">
                    {t("Show a congratulatory message when a customer crosses a\n                    milestone tier, before displaying the next goal.")}
                  </Text>
                </BlockStack>
                <Checkbox
                  label={t("Enabled")}
                  labelHidden
                  checked={celebrationEnabled}
                  onChange={setCelebrationEnabled}
                />
              </InlineStack>

              {celebrationEnabled && (
                <BlockStack gap="300">
                  <TextField
                    label={t("Celebration message")}
                    value={celebrationMessage}
                    onChange={setCelebrationMessage}
                    autoComplete="off"
                    helpText={t("Variables: {{nearest_reward}}")}
                  />

                  {milestones.map((m, index) => (
                    <TextField
                      key={`title-${m.id}`}
                      label={`Title before achieving milestone ${index + 1}`}
                      value={getMilestoneTitle(m)}
                      onChange={(v) => setMilestoneTitle(m.id, v)}
                      autoComplete="off"
                      helpText={t("Variables: {{amount_left}}, {{nearest_reward}}")}
                    />
                  ))}

                  {milestones.map((m, index) => (
                    <TextField
                      key={`reward-${m.id}`}
                      label={`Reward ${index + 1}`}
                      value={getRewardText(m)}
                      onChange={(v) => setRewardText(m.id, v)}
                      autoComplete="off"
                      helpText={
                        m.type === 'order_discount'
                          ? 'Variables: {{discount}}'
                          : undefined
                      }
                    />
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </BlockStack>

        <BlockStack gap="400">
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                {t("Preview")}
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                {t("Product page block · drag to view progress")}
              </Text>
              <input
                type="range"
                min={0}
                max={100}
                value={previewProgress}
                onChange={(e) => setPreviewProgress(Number(e.target.value))}
                style={{ width: '100%' }}
              />
              {/* Matches theme extension: Freebi Milestone Progress (PDP) */}
              <div
                style={{
                  background: '#F5F0E6',
                  borderRadius: 16,
                  padding: '20px 16px 16px',
                  color: '#1a1a1a',
                }}
              >
                <p
                  style={{
                    margin: '0 0 18px',
                    textAlign: 'center',
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: 1.35,
                  }}
                >
                  {(() => {
                    const next = nextMilestone;
                    const nearest = next
                      ? fillRewardOnly(getRewardText(next), next)
                      : '';
                    const idx = nearest ? previewTitle.lastIndexOf(nearest) : -1;
                    if (idx === -1) return previewTitle;
                    return (
                      <>
                        {previewTitle.slice(0, idx)}
                        <strong>{nearest}</strong>
                        {previewTitle.slice(idx + nearest.length)}
                      </>
                    );
                  })()}
                </p>
                <div style={{ position: 'relative', padding: '0 4px 4px' }}>
                  <div
                    style={{
                      position: 'absolute',
                      left: 20,
                      right: 20,
                      top: 13,
                      height: 3,
                      background: 'rgba(0,0,0,.12)',
                      borderRadius: 99,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${previewProgress}%`,
                        height: '100%',
                        background: '#1a1a1a',
                        borderRadius: 99,
                        transition: 'width .3s ease',
                      }}
                    />
                  </div>
                  <div
                    style={{
                      position: 'relative',
                      display: 'flex',
                      justifyContent: 'space-between',
                      zIndex: 1,
                    }}
                  >
                    {sortedForPreview.map((m) => {
                      const unlocked =
                        simulatedCart >= (Number(m.threshold) || 0);
                      const isNext = nextMilestone?.id === m.id;
                      const label = fillRewardOnly(getRewardText(m), m);
                      return (
                        <div
                          key={m.id}
                          style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            minWidth: 0,
                            textAlign: 'center',
                          }}
                        >
                          <div
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 12,
                              fontWeight: 700,
                              background: unlocked ? '#1a1a1a' : '#fff',
                              border: `2.5px solid ${
                                unlocked || isNext ? '#1a1a1a' : 'rgba(0,0,0,.2)'
                              }`,
                              color: unlocked ? '#fff' : '#1a1a1a',
                            }}
                          >
                            {unlocked
                              ? '✓'
                              : m.type === 'free_gift'
                                ? '🎁'
                                : m.type === 'free_shipping'
                                  ? '🚚'
                                  : '%'}
                          </div>
                          <span
                            style={{
                              marginTop: 8,
                              fontSize: 11,
                              fontWeight: unlocked || isNext ? 600 : 500,
                              color:
                                unlocked || isNext ? '#1a1a1a' : '#6b6b6b',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '100%',
                              padding: '0 2px',
                            }}
                          >
                            {label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {sortedForPreview.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      marginTop: 14,
                      padding: '10px 12px',
                      background: 'rgba(255,255,255,.55)',
                      border: '1px solid rgba(0,0,0,.08)',
                      borderRadius: 999,
                      fontSize: 12,
                    }}
                  >
                    <span
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        background: '#fff',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      🎁
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      {tierBasis === 'product_quantity'
                        ? `Buy ${sortedForPreview[sortedForPreview.length - 1]?.threshold || 0} items · unlock all`
                        : `Spend ${formatMoney(Number(sortedForPreview[sortedForPreview.length - 1]?.threshold) || 0)} get all rewards`}
                    </span>
                    <span
                      style={{
                        fontWeight: 600,
                        textDecoration: 'underline',
                        flexShrink: 0,
                      }}
                    >
                      {t("See details")}
                    </span>
                  </div>
                )}
              </div>
              <Text as="p" tone="subdued" variant="bodySm">
                {t("Theme editor → Product template → Add block → Freebi Milestone\n                Progress")}
              </Text>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="200">
              <InlineStack align="space-between">
                <Text as="h3" variant="headingSm">
                  {t("Summary")}
                </Text>
                <Badge tone="success">{t("Active")}</Badge>
              </InlineStack>
              <Text as="p" fontWeight="semibold">
                {t("Tiered rewards based on")}{' '}
                {tierBasis === 'cart_value' ? 'cart value' : 'item count'} with{' '}
                {milestones.length} milestones
              </Text>
              <Text as="p" variant="bodySm" fontWeight="semibold">
                {t("Reward tiers")}
              </Text>
              {sortedForPreview.map((m) => (
                <InlineStack key={m.id} align="space-between">
                  <Text as="span" variant="bodySm">
                    {thresholdDisplay(m)}
                  </Text>
                  <Text as="span" variant="bodySm" fontWeight="semibold">
                    {fillRewardOnly(getRewardText(m), m)}
                  </Text>
                </InlineStack>
              ))}
              <Text as="p" tone="subdued" variant="bodySm">
                {t("Display · Product page · Auto-add gifts")}
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                {t("Method: Milestone reward")}
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                {t("Discount code:")} {discountCode || '—'}
              </Text>
            </BlockStack>
          </Card>
        </BlockStack>
      </div>
    );
  }

  const pageLabel = campaignPageLabel(isEdit, 'Milestone rewards');

  return (
    <Page
      title={pageLabel}
      subtitle={name.trim() || undefined}
      backAction={{ content: t("Campaigns"), onAction: () => navigate('/campaigns') }}
      primaryAction={
        !embedded
          ? {
              content: t("Save"),
              loading: saving,
              disabled: !dirty,
              onAction: () => void save(true),
            }
          : undefined
      }
    >
      <ContextualSaveBar
        id="freebi-milestone-save"
        open={embedded && dirty && !loadingCampaign}
        saving={saving}
        onSave={() => save(true)}
        onDiscard={discard}
      />
      <SavingOverlay
        open={saving}
        label={t("Saving campaign…")}
        detail="Syncing milestones and Shopify discounts"
      />
      <BlockStack gap="400">
        <CampaignNameEditor
          name={name}
          onChange={setName}
          placeholder={t("Milestone rewards")}
          defaultEditing={!isEdit && !name.trim()}
        />
        {loadingCampaign && <Banner tone="info">{t("Loading campaign…")}</Banner>}
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        {message && (
          <Banner tone="success" onDismiss={() => setMessage(null)}>
            {message}
          </Banner>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
            background: '#fff',
            borderRadius: 12,
            border: '1px solid #e1e3e5',
            padding: 6,
          }}
        >
          {([1, 2] as const).map((step) => (
            <button
              key={step}
              type="button"
              onClick={() => setMainStep(step)}
              style={{
                border: 'none',
                borderRadius: 8,
                padding: '12px 16px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
                background: mainStep === step ? '#e4e5e7' : 'transparent',
                color: '#202223',
              }}
            >
              {t("Step")} {step}:{' '}
              {step === 1 ? t('Campaign setting') : t('Display setting')}
            </button>
          ))}
        </div>

        {mainStep === 1 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) 280px',
              gap: 16,
              alignItems: 'start',
            }}
          >
            <div>{renderMilestones()}</div>
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  {t("Summary")}
                </Text>
                <Text as="p" fontWeight="semibold">
                  {milestones.length} {t("milestones ·")}{' '}
                  {tierBasis === 'cart_value' ? 'Cart value' : 'Quantity'}
                </Text>
                {sortedForPreview.map((m) => (
                  <InlineStack key={m.id} align="space-between">
                    <Text as="span" variant="bodySm">
                      {thresholdDisplay(m)}
                    </Text>
                    <Text as="span" variant="bodySm">
                      {rewardLabel(m)}
                    </Text>
                  </InlineStack>
                ))}
                <Button variant="primary" onClick={() => setMainStep(2)}>
                  {t("Next: Display")}
                </Button>
              </BlockStack>
            </Card>
          </div>
        ) : (
          <BlockStack gap="400">
            {renderDisplay()}
            <InlineStack align="start">
              <Button onClick={() => setMainStep(1)}>{t("Back to campaign settings")}</Button>
            </InlineStack>
          </BlockStack>
        )}
      </BlockStack>

      <LocationPickerModal
        open={locationPickerOpen}
        selectedCodes={locationCodes}
        onClose={() => setLocationPickerOpen(false)}
        onConfirm={(selection) => {
          setLocationCodes(selection.map((s) => s.code));
          setLocationLabels(
            Object.fromEntries(selection.map((s) => [s.code, s.name])),
          );
        }}
      />

      <Modal
        open={addMilestoneOpen}
        onClose={() => setAddMilestoneOpen(false)}
        title={t("Add milestone")}
        secondaryActions={[
          { content: t("Cancel"), onAction: () => setAddMilestoneOpen(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p" tone="subdued">
              {t("Choose the reward type for this milestone.")}
            </Text>
            {(
              [
                {
                  type: 'order_discount' as RewardType,
                  title: 'Order discount',
                  desc: 'Percentage or fixed amount off the order',
                  disabled: false,
                },
                {
                  type: 'free_shipping' as RewardType,
                  title: 'Free shipping',
                  desc: freeShippingUsed
                    ? 'Already used — only one free shipping milestone is allowed'
                    : 'Remove shipping cost when threshold is met',
                  disabled: freeShippingUsed,
                },
                {
                  type: 'free_gift' as RewardType,
                  title: 'Free gift',
                  desc: 'Customer unlocks gift product(s)',
                  disabled: false,
                },
              ] as const
            ).map((opt) => (
              <button
                key={opt.type}
                type="button"
                disabled={opt.disabled}
                onClick={() => addMilestoneOfType(opt.type)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: 14,
                  borderRadius: 12,
                  border: '1px solid #e1e3e5',
                  background: opt.disabled ? '#f6f6f7' : '#fff',
                  cursor: opt.disabled ? 'not-allowed' : 'pointer',
                  opacity: opt.disabled ? 0.55 : 1,
                }}
              >
                <InlineStack gap="200" blockAlign="start">
                  <Text as="span">{TYPE_META[opt.type].icon}</Text>
                  <BlockStack gap="050">
                    <Text as="span" fontWeight="semibold">
                      {opt.title}
                    </Text>
                    <Text as="span" tone="subdued" variant="bodySm">
                      {opt.desc}
                    </Text>
                  </BlockStack>
                </InlineStack>
              </button>
            ))}
          </BlockStack>
        </Modal.Section>
      </Modal>

      <CatalogPickerModal
        open={!!giftBrowseFor}
        mode="products"
        title={t("Select gift products")}
        selectedIds={
          milestones
            .find((m) => m.id === giftBrowseFor)
            ?.giftProducts.flatMap((g) => {
              const vids = (g.selectedVariants || [])
                .map((v) => v.id)
                .filter(Boolean) as string[];
              // Variant picks → variant ids only (product id would mark all variants)
              if (vids.length) return vids;
              return [g.productId, g.id].filter(
                (x): x is string => Boolean(x && String(x).trim()),
              );
            }) || []
        }
        onClose={() => setGiftBrowseFor(null)}
        onToggle={toggleGiftFromPicker}
      />
    </Page>
  );
}
